import { QaAction, QaAnswer, QaAssignmentCommandType, QaAssignmentStatus, QaCaseCommand, QaCaseStatus, QaComplianceStatus, QaDeadlineKind, QaErrorCode, QaPassStatus, QaPermission, QaReviewerRole, QaRuleRevisionStatus, QaVisibilityActor, QaVisibilityScope, } from './contracts.js';
import { QaDomainError } from './errors.js';
const PERCENTAGE_SCALE = 100;
const PERCENTAGE_DECIMAL_PLACES = 2;
const JOHANNESBURG_UTC_OFFSET_MINUTES = 120;
const MILLISECONDS_PER_MINUTE = 60_000;
const FNV_OFFSET_BASIS = 2_166_136_261;
const FNV_PRIME = 16_777_619;
const CASE_TRANSITIONS = {
    [QaCaseStatus.PENDING_ASSIGNMENT]: {
        [QaCaseCommand.ASSIGN]: QaCaseStatus.IN_REVIEW,
        [QaCaseCommand.CANCEL]: QaCaseStatus.CANCELLED,
    },
    [QaCaseStatus.IN_REVIEW]: {
        [QaCaseCommand.MARK_READY]: QaCaseStatus.READY_TO_FINALIZE,
        [QaCaseCommand.CANCEL]: QaCaseStatus.CANCELLED,
    },
    [QaCaseStatus.READY_TO_FINALIZE]: {
        [QaCaseCommand.REOPEN_REVIEW]: QaCaseStatus.IN_REVIEW,
        [QaCaseCommand.FINALIZE]: QaCaseStatus.AWAITING_ACKNOWLEDGEMENT,
        [QaCaseCommand.CANCEL]: QaCaseStatus.CANCELLED,
    },
    [QaCaseStatus.AWAITING_ACKNOWLEDGEMENT]: {
        [QaCaseCommand.REOPEN_FINALIZATION]: QaCaseStatus.READY_TO_FINALIZE,
        [QaCaseCommand.CLOSE_ACKNOWLEDGED]: QaCaseStatus.CLOSED_ACKNOWLEDGED,
        [QaCaseCommand.CLOSE_UNACKNOWLEDGED]: QaCaseStatus.CLOSED_UNACKNOWLEDGED,
        [QaCaseCommand.DISPUTE]: QaCaseStatus.DISPUTED,
    },
    [QaCaseStatus.CLOSED_ACKNOWLEDGED]: {
        [QaCaseCommand.REOPEN_FINALIZATION]: QaCaseStatus.READY_TO_FINALIZE,
    },
    [QaCaseStatus.CLOSED_UNACKNOWLEDGED]: {
        [QaCaseCommand.REOPEN_FINALIZATION]: QaCaseStatus.READY_TO_FINALIZE,
    },
    [QaCaseStatus.DISPUTED]: {
        [QaCaseCommand.RESOLVE_DISPUTE]: QaCaseStatus.CLOSED_RESOLVED,
        [QaCaseCommand.REVISE_RESULT]: QaCaseStatus.AWAITING_ACKNOWLEDGEMENT,
        [QaCaseCommand.REOPEN_REVIEW]: QaCaseStatus.IN_REVIEW,
        [QaCaseCommand.REOPEN_FINALIZATION]: QaCaseStatus.READY_TO_FINALIZE,
    },
    [QaCaseStatus.CLOSED_RESOLVED]: {
        [QaCaseCommand.REOPEN_FINALIZATION]: QaCaseStatus.READY_TO_FINALIZE,
    },
    [QaCaseStatus.CANCELLED]: {},
};
const COMPLETE_COMPARISON_STATUSES = new Set([
    QaAssignmentStatus.SUBMITTED,
    QaAssignmentStatus.WAIVED,
]);
const ACTIVE_ASSIGNMENT_STATUSES = new Set([
    QaAssignmentStatus.ASSIGNED,
    QaAssignmentStatus.IN_PROGRESS,
    QaAssignmentStatus.SUBMITTED,
]);
const TERMINAL_ASSIGNMENT_STATUSES = new Set([
    QaAssignmentStatus.SUBMITTED,
    QaAssignmentStatus.WAIVED,
    QaAssignmentStatus.REASSIGNED,
]);
/** Keeps domain failures stable when transported through different APIs. */
function fail(code) {
    throw new QaDomainError(code);
}
/** Makes stored and displayed percentages deterministic at score boundaries. */
function roundHalfUp(value, decimalPlaces) {
    const factor = 10 ** decimalPlaces;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}
/** Rejects incomplete or forged inputs before they can affect a score. */
function validateScoreInput(input) {
    if (!Number.isFinite(input.passingPercentage) ||
        input.passingPercentage < 0 ||
        input.passingPercentage > PERCENTAGE_SCALE) {
        fail(QaErrorCode.INVALID_SCORECARD);
    }
    const questionIds = new Set();
    for (const question of input.questions) {
        if (questionIds.has(question.id) ||
            !Number.isFinite(question.points) ||
            question.points <= 0) {
            fail(QaErrorCode.INVALID_SCORECARD);
        }
        questionIds.add(question.id);
    }
    const answerIds = new Set();
    for (const answer of input.answers) {
        if (answerIds.has(answer.questionId) ||
            !questionIds.has(answer.questionId)) {
            fail(QaErrorCode.INVALID_SCORECARD);
        }
        answerIds.add(answer.questionId);
    }
    if (answerIds.size !== questionIds.size) {
        fail(QaErrorCode.INVALID_SCORECARD);
    }
}
/** Owns score calculation so clients can never submit authoritative totals. */
export function scoreReview(input) {
    validateScoreInput(input);
    const answers = new Map(input.answers.map((answer) => [answer.questionId, answer.answer]));
    let earnedPoints = 0;
    let applicablePoints = 0;
    let complianceStatus = QaComplianceStatus.PASSED;
    const categoryScores = new Map();
    for (const question of input.questions) {
        const answer = answers.get(question.id);
        if (answer === undefined) {
            fail(QaErrorCode.INVALID_SCORECARD);
        }
        const category = question.categoryId
            ? (categoryScores.get(question.categoryId) ?? {
                categoryId: question.categoryId,
                categoryName: question.categoryName ?? question.categoryId,
                earnedPoints: 0,
                applicablePoints: 0,
            })
            : undefined;
        if (category) {
            categoryScores.set(category.categoryId, category);
        }
        if (answer === QaAnswer.NOT_APPLICABLE) {
            if (!question.allowNotApplicable) {
                fail(QaErrorCode.INVALID_SCORECARD);
            }
            continue;
        }
        applicablePoints += question.points;
        if (category) {
            category.applicablePoints += question.points;
        }
        if (answer === QaAnswer.YES) {
            earnedPoints += question.points;
            if (category) {
                category.earnedPoints += question.points;
            }
        }
        if (answer === QaAnswer.NO && question.critical) {
            complianceStatus = QaComplianceStatus.FAILED;
        }
    }
    const categorySummaries = [...categoryScores.values()].map((category) => ({
        ...category,
        percentage: category.applicablePoints === 0
            ? null
            : roundHalfUp((category.earnedPoints / category.applicablePoints) *
                PERCENTAGE_SCALE, PERCENTAGE_DECIMAL_PLACES),
    }));
    if (applicablePoints === 0) {
        return {
            earnedPoints,
            applicablePoints,
            percentage: null,
            passStatus: QaPassStatus.UNSCORABLE,
            complianceStatus,
            categorySummaries,
        };
    }
    const percentage = roundHalfUp((earnedPoints / applicablePoints) * PERCENTAGE_SCALE, PERCENTAGE_DECIMAL_PLACES);
    return {
        earnedPoints,
        applicablePoints,
        percentage,
        passStatus: percentage >= input.passingPercentage
            ? QaPassStatus.PASSED
            : QaPassStatus.FAILED,
        complianceStatus,
        categorySummaries,
    };
}
/** Recomputes the official result while making every override explainable. */
export function reconcileOfficialResult(input) {
    const officialAnswers = new Map(input.officialAnswers.map((answer) => [answer.questionId, answer.answer]));
    const questionIds = new Set(input.questions.map((question) => question.id));
    if (officialAnswers.size !== questionIds.size ||
        [...officialAnswers.keys()].some((questionId) => !questionIds.has(questionId))) {
        fail(QaErrorCode.INVALID_RECONCILIATION);
    }
    const changedQuestionIds = [];
    for (const answer of input.reconciledAnswers) {
        const officialAnswer = officialAnswers.get(answer.questionId);
        if (officialAnswer === undefined) {
            fail(QaErrorCode.INVALID_RECONCILIATION);
        }
        if (answer.answer !== officialAnswer) {
            if (!answer.changeReason || answer.changeReason.trim() === '') {
                fail(QaErrorCode.INVALID_RECONCILIATION);
            }
            changedQuestionIds.push(answer.questionId);
        }
    }
    let score;
    try {
        score = scoreReview({
            questions: input.questions,
            answers: input.reconciledAnswers,
            passingPercentage: input.passingPercentage,
        });
    }
    catch (error) {
        if (error instanceof QaDomainError) {
            fail(QaErrorCode.INVALID_RECONCILIATION);
        }
        throw error;
    }
    return { ...score, changedQuestionIds };
}
/** Shows reviewer disagreement without making it an authoritative score. */
export function calculateReviewerVariance(percentages) {
    const scorable = percentages.filter((percentage) => percentage !== null);
    if (scorable.length < 2) {
        return 0;
    }
    return roundHalfUp(Math.max(...scorable) - Math.min(...scorable), PERCENTAGE_DECIMAL_PLACES);
}
/** Enforces one case state machine across every server workflow. */
export function transitionCase(status, command) {
    const nextStatus = CASE_TRANSITIONS[status][command];
    if (nextStatus === undefined) {
        fail(QaErrorCode.INVALID_TRANSITION);
    }
    return nextStatus;
}
/** Prevents finalization until all current required reviews are complete. */
export function isCaseReady(assignments) {
    const currentOfficialAssignments = assignments.filter((assignment) => assignment.role === QaReviewerRole.OFFICIAL &&
        assignment.status !== QaAssignmentStatus.REASSIGNED);
    if (currentOfficialAssignments.length === 0 ||
        currentOfficialAssignments.some((assignment) => assignment.status !== QaAssignmentStatus.SUBMITTED)) {
        return false;
    }
    return assignments.every((assignment) => {
        if (assignment.role === QaReviewerRole.OFFICIAL ||
            !assignment.required ||
            assignment.status === QaAssignmentStatus.REASSIGNED) {
            return true;
        }
        return COMPLETE_COMPARISON_STATUSES.has(assignment.status);
    });
}
/** Enforces assignment transitions and role-specific waiver policy. */
export function transitionAssignment(status, command, role) {
    if (command.type === QaAssignmentCommandType.WAIVE) {
        if (role === QaReviewerRole.OFFICIAL || command.reason.trim() === '') {
            fail(QaErrorCode.INVALID_ASSIGNMENT);
        }
    }
    if (status === QaAssignmentStatus.ASSIGNED) {
        if (command.type === QaAssignmentCommandType.START) {
            return QaAssignmentStatus.IN_PROGRESS;
        }
        if (command.type === QaAssignmentCommandType.WAIVE) {
            return QaAssignmentStatus.WAIVED;
        }
        if (command.type === QaAssignmentCommandType.REASSIGN) {
            return QaAssignmentStatus.REASSIGNED;
        }
    }
    if (status === QaAssignmentStatus.IN_PROGRESS) {
        if (command.type === QaAssignmentCommandType.SUBMIT) {
            return QaAssignmentStatus.SUBMITTED;
        }
        if (command.type === QaAssignmentCommandType.WAIVE) {
            return QaAssignmentStatus.WAIVED;
        }
        if (command.type === QaAssignmentCommandType.REASSIGN) {
            return QaAssignmentStatus.REASSIGNED;
        }
    }
    if (status === QaAssignmentStatus.SUBMITTED &&
        command.type === QaAssignmentCommandType.REOPEN) {
        return QaAssignmentStatus.IN_PROGRESS;
    }
    return fail(QaErrorCode.INVALID_TRANSITION);
}
/** Derives overdue state to avoid persisting a flag that becomes stale. */
export function isAssignmentOverdue(dueAt, status, at) {
    if (TERMINAL_ASSIGNMENT_STATUSES.has(status)) {
        return false;
    }
    return at.getTime() > dueAt.getTime();
}
/** Excludes inactive or out-of-window rules from call selection. */
function isRevisionEffective(revision, at) {
    return (revision.status === QaRuleRevisionStatus.ACTIVE &&
        revision.effectiveFrom.getTime() <= at.getTime() &&
        (revision.effectiveTo === null ||
            revision.effectiveTo.getTime() >= at.getTime()));
}
/** Uses stable IDs and organization scope instead of mutable campaign names. */
function revisionMatchesCall(revision, call) {
    if (revision.organizationId !== call.organizationId) {
        return false;
    }
    return revision.campaignGroups.some((group) => group.campaignId === call.campaignId &&
        (group.dispositionIds.length === 0 ||
            group.dispositionIds.includes(call.dispositionId)));
}
/** Selects one deterministic rule and rejects ambiguous equal priorities. */
export function matchSamplingRule(call, revisions, at) {
    const matches = revisions.filter((revision) => isRevisionEffective(revision, at) && revisionMatchesCall(revision, call));
    if (matches.length === 0) {
        return null;
    }
    const highestPriority = Math.min(...matches.map((revision) => revision.priority));
    const highestPriorityMatches = matches.filter((revision) => revision.priority === highestPriority);
    if (highestPriorityMatches.length > 1) {
        fail(QaErrorCode.RULE_CONFLICT);
    }
    return highestPriorityMatches[0];
}
/** Detects revisions that can govern calls during the same time window. */
function effectivePeriodsOverlap(left, right) {
    const leftEnd = left.effectiveTo?.getTime() ?? Number.POSITIVE_INFINITY;
    const rightEnd = right.effectiveTo?.getTime() ?? Number.POSITIVE_INFINITY;
    return (left.effectiveFrom.getTime() <= rightEnd &&
        right.effectiveFrom.getTime() <= leftEnd);
}
/** Treats an empty disposition list as the campaign-wide wildcard. */
function dispositionsOverlap(left, right) {
    if (left.length === 0 || right.length === 0) {
        return true;
    }
    return left.some((dispositionId) => right.includes(dispositionId));
}
/** Finds ambiguous rule configuration before revisions are activated. */
export function findRuleConflicts(revisions) {
    const conflicts = [];
    for (let leftIndex = 0; leftIndex < revisions.length; leftIndex += 1) {
        const left = revisions[leftIndex];
        for (let rightIndex = leftIndex + 1; rightIndex < revisions.length; rightIndex += 1) {
            const right = revisions[rightIndex];
            if (left.organizationId !== right.organizationId ||
                left.priority !== right.priority ||
                !effectivePeriodsOverlap(left, right)) {
                continue;
            }
            const conflictingCampaignIds = new Set();
            for (const leftGroup of left.campaignGroups) {
                for (const rightGroup of right.campaignGroups) {
                    if (leftGroup.campaignId === rightGroup.campaignId &&
                        dispositionsOverlap(leftGroup.dispositionIds, rightGroup.dispositionIds)) {
                        conflictingCampaignIds.add(leftGroup.campaignId);
                    }
                }
            }
            for (const campaignId of conflictingCampaignIds) {
                conflicts.push({
                    leftRevisionId: left.id,
                    rightRevisionId: right.id,
                    campaignId,
                });
            }
        }
    }
    return conflicts;
}
/** Balances sampling targets with operational minimums and available calls. */
export function calculateAgentQuota(input) {
    const values = [
        input.eligibleCalls,
        input.availableCalls,
        input.sampleRate,
        input.minimum,
        input.maximum,
    ];
    if (values.some((value) => !Number.isFinite(value) || value < 0) ||
        input.sampleRate > 1 ||
        input.minimum > input.maximum) {
        fail(QaErrorCode.INVALID_QUOTA);
    }
    if (input.eligibleCalls === 0 || input.availableCalls === 0) {
        return 0;
    }
    const sampledCalls = Math.ceil(input.sampleRate * input.eligibleCalls);
    const boundedCalls = Math.min(Math.max(sampledCalls, input.minimum), input.maximum);
    return Math.min(boundedCalls, input.eligibleCalls, input.availableCalls);
}
/** Provides a portable rank without runtime-dependent random state. */
function hashString(value) {
    let hash = FNV_OFFSET_BASIS;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, FNV_PRIME);
    }
    return hash >>> 0;
}
/** Makes sampled candidate order reproducible for audits and retries. */
export function rankCandidates(callIds, seed) {
    return [...callIds].sort((left, right) => {
        const rankDifference = hashString(`${seed}\u0000${left}`) - hashString(`${seed}\u0000${right}`);
        return rankDifference === 0 ? left.localeCompare(right) : rankDifference;
    });
}
/** Maps each workflow deadline to the matching policy field once. */
function deadlineProperty(kind) {
    switch (kind) {
        case QaDeadlineKind.REVIEWER:
            return 'reviewerDays';
        case QaDeadlineKind.FINALIZER:
            return 'finalizerDays';
        case QaDeadlineKind.ACKNOWLEDGEMENT:
            return 'acknowledgementDays';
    }
}
/** Preserves Johannesburg wall-clock time when adding business calendar days. */
function addJohannesburgCalendarDays(startsAt, days) {
    const offsetMilliseconds = JOHANNESBURG_UTC_OFFSET_MINUTES * MILLISECONDS_PER_MINUTE;
    const localDate = new Date(startsAt.getTime() + offsetMilliseconds);
    localDate.setUTCDate(localDate.getUTCDate() + days);
    return new Date(localDate.getTime() - offsetMilliseconds);
}
/** Applies case-to-fallback precedence consistently for every deadline kind. */
export function resolveDeadline(input) {
    const property = deadlineProperty(input.kind);
    const days = input.casePolicy?.[property] ??
        input.rulePolicy?.[property] ??
        input.organizationPolicy?.[property] ??
        input.fallbackPolicy[property];
    if (days === undefined ||
        !Number.isInteger(days) ||
        days <= 0 ||
        Number.isNaN(input.startsAt.getTime())) {
        fail(QaErrorCode.INVALID_DEADLINE);
    }
    return addJohannesburgCalendarDays(input.startsAt, days);
}
/** Closes acknowledgement only when its deadline makes the outcome final. */
export function resolveAcknowledgementClosure(input) {
    if (input.status !== QaCaseStatus.AWAITING_ACKNOWLEDGEMENT) {
        fail(QaErrorCode.INVALID_TRANSITION);
    }
    if (input.at.getTime() < input.deadline.getTime()) {
        return QaCaseStatus.AWAITING_ACKNOWLEDGEMENT;
    }
    return input.acknowledgedAt !== null &&
        input.acknowledgedAt.getTime() <= input.deadline.getTime()
        ? QaCaseStatus.CLOSED_ACKNOWLEDGED
        : QaCaseStatus.CLOSED_UNACKNOWLEDGED;
}
/** Returns one denial shape without leaking which access check failed. */
function denyAccess() {
    return { allowed: false, code: QaErrorCode.FORBIDDEN };
}
/** Keeps permission checks exact and independent from staff identity. */
function hasPermission(context, permission) {
    return context.permissions.includes(permission);
}
/** Revokes assignment-derived access after waiver or reassignment. */
function hasActiveAssignment(context, caseId) {
    if (caseId === null) {
        return false;
    }
    return context.assignmentGrants.some((grant) => grant.caseId === caseId && ACTIVE_ASSIGNMENT_STATUSES.has(grant.status));
}
/** Applies organization scope before evaluating action-specific grants. */
export function authorizeQaAction(context, resource, action) {
    if (context.organizationId !== resource.organizationId) {
        return denyAccess();
    }
    const assignedReviewer = hasActiveAssignment(context, resource.caseId);
    const designatedFinalizer = context.userId === resource.finalizerId;
    const matchingTeam = resource.teamId !== null && context.ledTeamIds.includes(resource.teamId);
    const publishedResultViewer = resource.resultPublished &&
        (context.userId === resource.evaluatedAgentId ||
            (matchingTeam && hasPermission(context, QaPermission.VIEW_TEAM)) ||
            hasPermission(context, QaPermission.VIEW_ORGANIZATION));
    switch (action) {
        case QaAction.REVIEW_CASE:
            return assignedReviewer && hasPermission(context, QaPermission.REVIEW)
                ? { allowed: true }
                : denyAccess();
        case QaAction.FINALIZE_CASE:
            return designatedFinalizer &&
                hasPermission(context, QaPermission.FINALIZE)
                ? { allowed: true }
                : denyAccess();
        case QaAction.VIEW_RESULT:
            return publishedResultViewer ? { allowed: true } : denyAccess();
        case QaAction.VIEW_CASE:
            return (assignedReviewer &&
                hasPermission(context, QaPermission.REVIEW)) ||
                (designatedFinalizer &&
                    hasPermission(context, QaPermission.FINALIZE)) ||
                (matchingTeam && hasPermission(context, QaPermission.VIEW_TEAM)) ||
                hasPermission(context, QaPermission.VIEW_ORGANIZATION)
                ? { allowed: true }
                : denyAccess();
        case QaAction.PLAY_RECORDING:
            return hasPermission(context, QaPermission.LIST_RECORDINGS) ||
                (assignedReviewer && hasPermission(context, QaPermission.REVIEW)) ||
                (designatedFinalizer &&
                    hasPermission(context, QaPermission.FINALIZE)) ||
                publishedResultViewer
                ? { allowed: true }
                : denyAccess();
    }
}
/** Starts every response projection hidden to protect blind-review data. */
function hiddenVisibility() {
    return {
        assignmentScope: QaVisibilityScope.NONE,
        answerScope: QaVisibilityScope.NONE,
        reviewerIdentities: false,
        reviewerProgress: false,
        reviewerNotes: false,
        reviewerScores: false,
        publishedResult: false,
        recordingLocator: false,
        operationsStatus: false,
        operationsCounts: false,
    };
}
/** Reveals only the fields allowed for the current QA audience. */
export function resolveQaVisibility(context, qaCase) {
    const visibility = hiddenVisibility();
    switch (context.actor) {
        case QaVisibilityActor.REVIEWER:
            return {
                ...visibility,
                assignmentScope: QaVisibilityScope.OWN,
                answerScope: QaVisibilityScope.OWN,
            };
        case QaVisibilityActor.FINALIZER:
            if (qaCase.readyToFinalize &&
                qaCase.status === QaCaseStatus.READY_TO_FINALIZE) {
                return {
                    ...visibility,
                    assignmentScope: QaVisibilityScope.ALL,
                    answerScope: QaVisibilityScope.ALL,
                    reviewerIdentities: true,
                    reviewerProgress: true,
                    reviewerNotes: true,
                    reviewerScores: true,
                };
            }
            return visibility;
        case QaVisibilityActor.EVALUATED_AGENT:
            return { ...visibility, publishedResult: qaCase.resultPublished };
        case QaVisibilityActor.ORGANIZATION_OPERATIONS:
            return {
                ...visibility,
                operationsStatus: true,
                operationsCounts: true,
            };
    }
    return visibility;
}
